/** SB-612: Remove duplicate image names only when the link already supplies
 * that name. Image-only links retain their alternative text.
 */
(() => {
  'use strict';
  if (window.sbRedundantImageNames) return;
  window.sbRedundantImageNames = true;
  const changed = new WeakMap();
  const pending = new Set();
  let scheduled = false;
  /**
   * Normalize case and whitespace for exact image/link name comparisons.
   * @param {string} text - Accessible label or image alternative text.
   * @returns {string} Normalized comparison value.
   */
  const normalize = text => text.trim().replace(/\s+/g, ' ').toLowerCase();
  /**
   * Suppress duplicate image text only while the link supplies the same name.
   * Restore owned alt changes when that name disappears; preserve image-only links.
   * @param {HTMLAnchorElement} link - Connected link whose images need reconciliation.
   */
  function enhance(link) {
    if (!link.isConnected) return;
    // Exclude hidden icon titles and other text that is not the link's name.
    const walker = document.createTreeWalker(link, NodeFilter.SHOW_TEXT);
    let text = '';
    while (walker.nextNode()) {
      let parent = walker.currentNode.parentElement;
      let hidden = false;
      while (parent && parent !== link) {
        if (parent.matches('svg, [aria-hidden="true"], [hidden], script, style') ||
            getComputedStyle(parent).display === 'none' || getComputedStyle(parent).visibility === 'hidden') {
          hidden = true;
          break;
        }
        parent = parent.parentElement;
      }
      if (!hidden) text += walker.currentNode.textContent;
    }
    const label = normalize(link.getAttribute('aria-label') || text);
    link.querySelectorAll('img').forEach(img => {
      if (changed.has(img) && img.getAttribute('alt') !== '') changed.delete(img);
      const original = changed.has(img) ? changed.get(img) : img.getAttribute('alt');
      const redundant = !!label && !!original && normalize(original) === label;
      if (redundant && img.getAttribute('alt') !== '') {
        changed.set(img, original);
        img.setAttribute('alt', '');
      } else if (!redundant && changed.has(img)) {
        img.setAttribute('alt', original);
        changed.delete(img);
      }
    });
  }
  /**
   * Batch the containing link and optionally image links in an added subtree.
   * @param {Node} node - Changed node or newly inserted subtree.
   * @param {boolean} [descend=true] - Scan descendants for discovery, not outer mutations.
   */
  function enqueue(node, descend = true) {
    if (node.nodeType !== 1) node = node.parentElement;
    if (!node) return;
    const link = node.closest('a');
    if (link) pending.add(link);
    if (descend) node.querySelectorAll('a').forEach(link => { if (link.querySelector('img')) pending.add(link); });
    if (scheduled || !pending.size) return;
    scheduled = true;
    queueMicrotask(() => {
      scheduled = false;
      const links = Array.from(pending);
      pending.clear();
      links.forEach(enhance);
    });
  }
  /** Enhance existing image links and observe local label changes and added content. */
  function initialize() {
    enqueue(document.body);
    new MutationObserver(records => records.forEach(record => {
      // Existing label changes affect only the containing link, not its neighbors.
      enqueue(record.target, false);
      record.addedNodes.forEach(node => enqueue(node));
    })).observe(document.body, { subtree: true, childList: true, characterData: true,
      attributes: true, attributeFilter: ['alt', 'aria-label', 'hidden', 'aria-hidden', 'style', 'class'] });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
})();
